import { useRef } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Typography } from '@kc-console/common-ui';
import { getDateTime } from '@kc-console/core';
import { IcErrorRhombusFill, IcSuccessCircleFill, IcWarningTriangleFill } from '@kep/kcds-icons';

import type { ReactNode } from 'react';
import type { PropsWithClassName } from '@kc-console/common-ui';

export type NoticeBannerVariant = 'success' | 'danger' | 'warning';

export interface NoticeBannerProps extends PropsWithClassName {
  message: ReactNode;
  variant: NoticeBannerVariant;
  dataUpdatedAt?: number;
}

/**
 * Status Board 전반에서 공용으로 사용하는 상단 요약 배너.
 * @description
 * - 좌측 아이콘 + 메시지, 우측 최종 업데이트 시각을 표시한다.
 * - variant에 따라 아이콘 종류와 색상이 바뀐다.
 * @example
 * <NoticeBanner
 *   variant="danger"
 *   message={<span>조회 기간에 미해결 이벤트가 <strong>1건</strong> 있습니다.</span>}
 *   dataUpdatedAt={Date.now()}
 * />
 */
const NoticeBanner = ({ className, message, variant, dataUpdatedAt }: NoticeBannerProps) => {
  const stableUpdatedAt = useRef(dataUpdatedAt);
  if (dataUpdatedAt) stableUpdatedAt.current = dataUpdatedAt;

  return (
    <BannerRoot className={className}>
      <BannerMain>
        <IconWrapper>{renderIcon(variant)}</IconWrapper>
        <Typography variant="label/regular/xl">{message}</Typography>
      </BannerMain>
      {!!stableUpdatedAt.current && (
        <BannerMeta>
          <Typography variant="label/regular/md" color="text/weaker/default">
            최종 업데이트: {getDateTime(new Date(stableUpdatedAt.current).toISOString())}
          </Typography>
        </BannerMeta>
      )}
    </BannerRoot>
  );
};

export default NoticeBanner;

const renderIcon = (variant: NoticeBannerVariant): ReactNode => {
  switch (variant) {
    case 'danger':
      return <IcErrorIcon fontSize={24} />;
    case 'warning':
      return <IcWarningIcon fontSize={24} />;
    case 'success':
    default:
      return <IcSuccessIcon fontSize={24} />;
  }
};

const BannerRoot = styled('div')(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    margin: 0 24px;
    border: 1px solid ${theme.color['border/weaker/default']};
    border-radius: 4px;
    background-color: ${theme.color['surface/default']};
    box-shadow: ${theme.elevation['level_1']};
  `,
);

const BannerMain = styled('div')`
  display: flex;
  gap: 6px;
  align-items: center;
  min-width: 0;
  padding: 16px 20px;
`;

const IconWrapper = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const BannerMeta = styled('div')`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 16px 20px;
`;

const IcSuccessIcon = styled(IcSuccessCircleFill)(
  ({ theme }) => css`
    color: ${theme.color['icon/success/default']};
  `,
);

const IcErrorIcon = styled(IcErrorRhombusFill)(
  ({ theme }) => css`
    color: ${theme.color['icon/danger/default']};
  `,
);

const IcWarningIcon = styled(IcWarningTriangleFill)(
  ({ theme }) => css`
    color: ${theme.color['icon/warning/default']};
  `,
);
